import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class ComBannerCarouselB2C implements Plugin {
  private readonly slides: HTMLElement[];
  private readonly bullets: HTMLElement[];
  private readonly prevButton: HTMLElement | null;
  private readonly nextButton: HTMLElement | null;
  private readonly playPauseButton: HTMLElement | null;
  private readonly autoplayDelay: number;
  private readonly prefersReducedMotion: boolean;

  private currentIndex = 0;
  private isPlaying = false;
  private autoplayTimer: ReturnType<typeof setInterval> | null = null;

  private readonly handlePrevClick = () => this.goTo(this.currentIndex - 1);
  private readonly handleNextClick = () => this.goTo(this.currentIndex + 1);
  private readonly handleTogglePlayClick = () => this.toggleAutoplay();

  constructor(private readonly element: HTMLElement) {
    this.slides = Array.from(
      element.querySelectorAll<HTMLElement>(".com-bannerCarouselB2C__slide"),
    );
    this.bullets = Array.from(
      element.querySelectorAll<HTMLElement>(".com-bannerCarouselB2C__bullet"),
    );
    this.prevButton = element.querySelector<HTMLElement>(
      '[data-action="prev"]',
    );
    this.nextButton = element.querySelector<HTMLElement>(
      '[data-action="next"]',
    );
    this.playPauseButton = element.querySelector<HTMLElement>(
      '[data-action="toggle-play"]',
    );
    this.autoplayDelay = Number(element.dataset.autoplayDelay) || 5000;
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
  }

  init(): void {
    if (this.slides.length < 2) return;

    this.bullets.forEach((bullet, index) => {
      bullet.addEventListener("click", () => this.goTo(index));
    });

    this.prevButton?.addEventListener("click", this.handlePrevClick);
    this.nextButton?.addEventListener("click", this.handleNextClick);
    this.playPauseButton?.addEventListener("click", this.handleTogglePlayClick);

    if (this.playPauseButton && !this.prefersReducedMotion) {
      this.startAutoplay();
    } else if (this.playPauseButton) {
      this.setPlayPauseState(false);
    }
  }

  private goTo(index: number): void {
    const nextIndex = (index + this.slides.length) % this.slides.length;
    if (nextIndex === this.currentIndex) return;

    this.slides[this.currentIndex].classList.remove("is-active");
    this.slides[this.currentIndex].setAttribute("aria-hidden", "true");
    this.bullets[this.currentIndex]?.classList.remove("is-active");
    this.bullets[this.currentIndex]?.setAttribute("aria-current", "false");

    this.currentIndex = nextIndex;

    this.slides[this.currentIndex].classList.add("is-active");
    this.slides[this.currentIndex].setAttribute("aria-hidden", "false");
    this.bullets[this.currentIndex]?.classList.add("is-active");
    this.bullets[this.currentIndex]?.setAttribute("aria-current", "true");

    this.element.dispatchEvent(
      new CustomEvent("comBannerCarouselB2C:change", {
        bubbles: true,
        detail: { index: this.currentIndex },
      }),
    );
  }

  private startAutoplay(): void {
    this.setPlayPauseState(true);
    this.autoplayTimer = setInterval(() => {
      this.goTo(this.currentIndex + 1);
    }, this.autoplayDelay);
  }

  private stopAutoplay(): void {
    this.setPlayPauseState(false);
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  private toggleAutoplay(): void {
    if (this.isPlaying) {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  }

  private setPlayPauseState(isPlaying: boolean): void {
    this.isPlaying = isPlaying;
    if (!this.playPauseButton) return;

    const icon = this.playPauseButton.querySelector<HTMLElement>(
      ".com-bannerCarouselB2C__playPauseIcon",
    );

    icon?.classList.toggle("dsc-co-icon-pause", isPlaying);
    icon?.classList.toggle("dsc-co-icon-play", !isPlaying);
    this.playPauseButton.setAttribute(
      "aria-label",
      isPlaying ? "Pausar carrusel" : "Reanudar carrusel",
    );
  }

  destroy(): void {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    this.prevButton?.removeEventListener("click", this.handlePrevClick);
    this.nextButton?.removeEventListener("click", this.handleNextClick);
    this.playPauseButton?.removeEventListener(
      "click",
      this.handleTogglePlayClick,
    );
  }
}

PluginRegistry.register("comBannerCarouselB2C", ComBannerCarouselB2C);
